import {
  GetPostcodeDataResponse,
  FindAddressResponse,
  GetECADDataResponse,
} from "@/types/autoaddress";

import { PbAddressResponse } from "@/types/pitneyBowes";
import {
  AnyLocation,
  IAutoaddressAddress,
  ILocation,
  IPreciselyAddress,
  locationType,
} from "@/types/location";
import { get } from "lodash-es";
import {
  AddressLocation,
  Data8AddressRetrieveResponse,
  DatasetRecord,
} from "@/types";
import { CoordinateLocation } from "@/types/location/CoordinateLocation";
import { DatasetLocation } from "@/types/location/DatasetLocation";
import { GeometryLocation } from "@/types/location/GeometryLocation";

const getPbAddressArray = (value: PbAddressResponse): string[] =>
  (value.formattedStreetAddress || value.formattedLocationAddress
    ? [value.formattedStreetAddress, value.formattedLocationAddress]
    : ["streetName", "addressLastLine"].map((key) => get(value.address, [key]))
  ).filter(Boolean);

export const preciselyAddressResult = (value: PbAddressResponse): ILocation => {
  const preciselyAddress: IPreciselyAddress = {
    country: "GBR",
    addressProvider: "Precisely",
    address: getPbAddressArray(value),
    postcode: value.address.postCode1,
    precisionLevel: value.precisionLevel || 16,
    uprn: parseInt(value.ranges[0]?.customValues.UPRN),
  };
  return {
    type: "Address",
    geometry: {
      type: "Point",
      coordinates: value.geometry.coordinates,
    },
    address: preciselyAddress,
  };
};

export const data8ResultToLocation = (
  response: Data8AddressRetrieveResponse
): ILocation => {
  const { AdditionalData } = response.Result.RawAddress;
  const isBuildingLevel =
    AdditionalData &&
    AdditionalData.some(
      (x) => x.Name === "LatLongType" && x.Value === "Building"
    );

  const address: IPreciselyAddress = {
    country: "GBR",
    addressProvider: "Precisely",
    address: response.Result.Address.Lines,
    postcode: response.Result.RawAddress.Postcode,

    precisionLevel: isBuildingLevel ? 16 : 5,
    uprn: parseInt(response.Result.RawAddress.UniqueReference),
  };

  return new AddressLocation({
    type: "Address",
    address,
    geometry: {
      type: "Point",
      coordinates: [
        response.Result.RawAddress.Location.Longitude,
        response.Result.RawAddress.Location.Latitude,
      ],
    },
  });
};

export function createLocationInstance(
  location: ILocation
): AnyLocation | null {
  switch (location?.type) {
    case "Coordinate":
      return new CoordinateLocation(location);
    case "Address":
      return new AddressLocation(location);
    case "Claim":
    case "Policy":
      return new DatasetLocation(
        location as { record: DatasetRecord; type: "Policy" | "Claim" }
      );

    case "Geometry":
      return new GeometryLocation({
        geometry: location.geometry,
        type: location.type,
      } as {
        type: locationType;
        geometry: GeoJSON.Geometry;
      });
    default:
      return null;
  }
}

export const autoaddressAddressResult = ({
  findAddress,
  data,
}: {
  findAddress: FindAddressResponse;
  data: GetECADDataResponse | GetPostcodeDataResponse;
}): ILocation => {
  const addressArr = findAddress.vanityAddress
    ? findAddress.vanityAddress
    : findAddress.geographicAddress
    ? findAddress.geographicAddress
    : findAddress.postalAddress;

  const autoaddressAddress: IAutoaddressAddress =
    findAddress.input.country.toLowerCase() === "ni"
      ? {
          country: "NI",
          addressProvider: "Autoaddress",
          address: addressArr,
          postcode: findAddress.postcode,
          matchLevel: findAddress.matchLevel?.code,
          matchResult: findAddress.result?.code,
        }
      : {
          country: "IRL",
          addressProvider: "Autoaddress",
          address: addressArr,
          postcode: findAddress.postcode,
          ecadId: findAddress.addressId,
          matchLevel: findAddress.matchLevel?.code,
          matchResult: findAddress.result?.code,
        };
  return {
    type: "Address",
    geometry: {
      type: "Point",
      coordinates: [
        findAddress.input.country.toLowerCase() === "ni"
          ? data.spatialInfo.wgs84.location.longitude
          : data.spatialInfo.etrs89.location.longitude,
        findAddress.input.country.toLowerCase() === "ni"
          ? data.spatialInfo.wgs84.location.latitude
          : data.spatialInfo.etrs89.location.latitude,
      ],
    },
    address: autoaddressAddress,
  };
};
